import {useCallback, useState} from 'react';

import {useUpdateConfigMutation} from '~/store/rtkQuery';

type AskLunaAutoPublishConfig = {
  enabled: boolean;
  lastRun?: string;
};

export const ManageAskLunaAutoPublisher = ({
  config,
  onConfigUpdated,
}: {
  config: AskLunaAutoPublishConfig;
  onConfigUpdated: () => void;
}) => {
  const [updateConfig, {error}] = useUpdateConfigMutation();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingEnabled, setPendingEnabled] = useState<boolean | null>(null);

  const handleToggle = useCallback((enabled: boolean) => {
    setPendingEnabled(enabled);
    setShowConfirmation(true);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (pendingEnabled === null) return;

    const response = await updateConfig({
      config: 'askLunaAutoPublish',
      action: 'update',
      data: {
        enabled: pendingEnabled,
      },
    });

    if (!('error' in response)) {
      onConfigUpdated();
      setShowConfirmation(false);
      setPendingEnabled(null);
    }
  }, [pendingEnabled, updateConfig, onConfigUpdated]);

  const handleCancel = useCallback(() => {
    setShowConfirmation(false);
    setPendingEnabled(null);
  }, []);

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  return (
    <>
      <div className="bg-base-100 p-4 rounded-lg border">
        <div className="flex items-center justify-between">
          <div>
            <span className="font-medium">Last Run:</span>
            <span className="ml-2 text-gray-600">
              {formatDate(config?.lastRun)}
            </span>
          </div>
          <div className="flex items-center space-x-4">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              config?.enabled 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              {config?.enabled ? 'Enabled' : 'Disabled'}
            </span>
            <button
              onClick={() => handleToggle(!config?.enabled)}
              className={`btn ${config?.enabled ? 'btn-error' : 'btn-success'}`}
            >
              {config?.enabled ? 'Disable' : 'Enable'}
            </button>
          </div>
        </div>

        {error && (
          <div role="alert" className="alert alert-error mt-4">
            <span>{error.message}</span>
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      {showConfirmation && (
        <dialog className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-4">
              {pendingEnabled ? 'Enable' : 'Disable'} Auto-Publisher
            </h3>
            <p className="py-4">
              {pendingEnabled 
                ? ' This will allow the system to automatically publish questions.'
                : ' This will stop the system from automatically publishing questions.'
              }
            </p>
            <div className="modal-action">
              <button className="btn" onClick={handleCancel}>
                Cancel
              </button>
              <button 
                className={`btn ${pendingEnabled ? 'btn-success' : 'btn-error'}`}
                onClick={handleConfirm}
              >
                {pendingEnabled ? 'Enable' : 'Disable'}
              </button>
            </div>
          </div>
        </dialog>
      )}
    </>
  );
};
